import React from "react";
import Modal, { ModalText, ModalLayout, MoreModalButtons } from "common/modal";
import { useTranslation } from "react-i18next";
import { ColProps, notification, Typography, Image } from "antd";
import smile from "assets/smilelex.png";
import quint from "assets/squintlex.png";
import "./Calendar.css";

const { Text, Link } = Typography;
const { ConfirmationText } = ModalText;
const { ModalRow } = ModalLayout;
const { ModalButtonYellow, ButtonContainer } = MoreModalButtons;

type AddToCalendarModalProps = {
  visible: boolean;
  webcalUrl: string;
  onCloseModal: () => void;
  /** Should use alternative design for AddToCalendarModal */
  useAlternative?: boolean;
};

const AddToCalendarModal = ({
  visible,
  webcalUrl,
  onCloseModal,
  useAlternative = false,
}: AddToCalendarModalProps) => {
  const { t } = useTranslation("class_banner");

  /** All the available calendar options to map out */
  const calendars = [
    {
      name: "GOOGLE",
      url: `https://calendar.google.com/calendar/r?cid=${webcalUrl}`,
    },
    {
      name: "OUTLOOK",
      url: webcalUrl,
    },
    {
      name: "APPLE",
      url: webcalUrl,
    },
    {
      name: "COPY LINK",
      url: webcalUrl,
    },
  ];

  /** Use different grid options based on useAlternative */
  const gridOptions: ColProps = useAlternative
    ? {}
    : { xs: 20, sm: 20, md: 12 };

  const openCopiedNotification = (blocked: boolean) => {
    notification.open({
      message: (
        <Text strong>
          {blocked
            ? "Clipboard access blocked"
            : "Successfully copied to Clipboard"}
        </Text>
      ),
      description: blocked ? (
        <>
          It looks like your browser has blocked access to your clipboard. Try
          copying the Webcal link manually instead{" "}
          <Link
            href="webcal://staging.flexiclasses.com/admin/calendar/2dsiAyLWaG_zZxZyLjAc.ics"
            target="_blank"
          >
            webcal://staging.flexiclasses.com/admin/calendar/2dsiAyLWaG_zZxZyLjAc.ics
          </Link>
        </>
      ) : (
        "Well done, you successfully copied the Webcal ICS link to your clipboard."
      ),
      duration: 5,
      icon: <Image width={40} src={blocked ? quint : smile} />,
    });
  };

  const onClickCopy = (calUrl: string) => {
    try {
      navigator.clipboard.writeText(calUrl);
      openCopiedNotification(false);
      onCloseModal();
    } catch {
      openCopiedNotification(true);
      onCloseModal();
    }
  };

  return (
    <Modal
      title={
        useAlternative ? t("ADD CLASSES TO MY CALENDAR") : t("MY CALENDAR")
      }
      visible={visible}
      footer={null}
      onCancel={onCloseModal}
      modalWidth={useAlternative ? "fit-content" : undefined}
    >
      <ConfirmationText>
        {useAlternative
          ? t("Add the schedule of my booked classes to my calendar")
          : t(
              "I want my future classes to automatically appear in my calendar."
            )}
      </ConfirmationText>
      <ModalRow gutter={24}>
        {calendars.map((cal) => {
          return (
            <ButtonContainer
              key={cal.name}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...gridOptions}
            >
              <ModalButtonYellow
                type="primary"
                htmlType="button"
                href={cal.name === "COPY LINK" ? undefined : cal.url}
                target="_blank"
                onClick={() =>
                  cal.name === "COPY LINK"
                    ? onClickCopy(cal.url)
                    : onCloseModal()
                }
              >
                {useAlternative ? `${cal.name} CALENDAR` : cal.name}
              </ModalButtonYellow>
            </ButtonContainer>
          );
        })}
      </ModalRow>
    </Modal>
  );
};

export default AddToCalendarModal;
